import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { ApiContext } from '@api/api';
import { Calendar } from 'primereact/calendar';
import {
  dayJsInterval,
  apiUtcDateFromDayjs,
  dayjsFromApiUtc,
  dayjsFromApiPrague,
  dayjsFromLocalDateString,
  formatDateFromDayjsWithLocale,
  dayEnumFromNumber,
  shortDateFormat,
} from '@utils/dateWithTz';
import {
  DayEnum,
  GlobalDayWithInterval,
  GlobalInterval,
  GlobalTimeslots,
  OpeningEntityTypeEnum,
  OpeningHourDefinitionsDTO,
  OpeningModeEnum,
} from '@api/ringil-api.v2';
import { isNullOrUndefined, validateHourString } from '@utils/utils';
import dayjs, { Dayjs } from 'dayjs';
import { CenteredRowWithGap, Clickable, Note, RowWithGap, RowWithSpaceBetween } from '@components/styles';
import { Button } from 'primereact/button';
import InputHour from '../Form/InputHour/InputHour';
import * as yup from 'yup';
import { ValidationError } from 'yup';
import ValidationDiv from '@utils/validation';
import { useStateCallback } from '@hooks/useStateCallback/useStateCallback.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import produce from 'immer';
import CheckboxWithLabel from '@components/obsolete/CheckboxWithLabel/CheckboxWithLabel.tsx';
import CompanyExceptionWarning from '@components/obsolete/CompanyExceptionWarning/CompanyExceptionWarning.tsx';
import { useTranslation } from 'react-i18next';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';

interface ComponentProps {
  entityId: string;
  entityType: OpeningEntityTypeEnum;
  timeslot: GlobalTimeslots;
  onChange: (timeslot: GlobalTimeslots) => void;
  validationErrors?: ValidationError[];
  validationPrefix?: string;
  externalLabel?: boolean;
  value?: GlobalDayWithInterval[];
  dataTestId?: string;
}

interface PrintedTimeslotDayTestIds {
  container?: string;
  date?: string;
  intervals?: string;
  timezone?: string;
  status?: string;
  openButton?: string;
  closedButton?: string;
  openNonStopCheckbox?: string;
  addWorkingHoursButton?: string;
  removeIntervalButton: (intervalIndex: number) => string | undefined;
  intervalFrom: (intervalIndex: number) => string | undefined;
  intervalTo: (intervalIndex: number) => string | undefined;
}

const getTimeslotTestIdPrefix = (dataTestId?: string) => {
  if (!dataTestId) return undefined;
  if (dataTestId.endsWith('-timeslotSelect')) {
    return dataTestId.replace(/-timeslotSelect$/, '-timeslot');
  }
  return dataTestId;
};

const getPrintedTimeslotDayTestIds = (prefix: string | undefined, dayIndex: number): PrintedTimeslotDayTestIds => ({
  container: prefix ? `${prefix}-${dayIndex}` : undefined,
  date: prefix ? `${prefix}-${dayIndex}-date` : undefined,
  intervals: prefix ? `${prefix}-${dayIndex}-intervals` : undefined,
  timezone: prefix ? `${prefix}-${dayIndex}-timezone` : undefined,
  status: prefix ? `${prefix}-${dayIndex}-status` : undefined,
  openButton: prefix ? `${prefix}-${dayIndex}-open-button` : undefined,
  closedButton: prefix ? `${prefix}-${dayIndex}-closed-button` : undefined,
  openNonStopCheckbox: prefix ? `${prefix}-${dayIndex}-open-non-stop-checkbox` : undefined,
  addWorkingHoursButton: prefix ? `${prefix}-${dayIndex}-add-working-hours-button` : undefined,
  removeIntervalButton: (intervalIndex: number) => prefix ? `${prefix}-${dayIndex}-interval-${intervalIndex}-remove-button` : undefined,
  intervalFrom: (intervalIndex: number) => prefix ? `${prefix}-${dayIndex}-interval-${intervalIndex}-from` : undefined,
  intervalTo: (intervalIndex: number) => prefix ? `${prefix}-${dayIndex}-interval-${intervalIndex}-to` : undefined,
});

const getTimeslotDay = (dayWithInterval: GlobalDayWithInterval): Dayjs => (
  dayWithInterval.utcDay ? dayjsFromApiUtc(dayWithInterval.utcDay) : dayjsFromApiPrague(dayWithInterval.day)
);

const getPrintedTimeslotIntervalsText = (dayWithInterval: GlobalDayWithInterval): string => {
  if (dayWithInterval.openingMode === OpeningModeEnum.OPEN) {
    return '00:00-23:59';
  }
  if (dayWithInterval.openingMode === OpeningModeEnum.CLOSED) {
    return '00:00-00:00';
  }

  return dayWithInterval.intervals
    ?.map(interval => `${interval.sinceHoursString}-${interval.tillHoursString}`)
    .join(', ') ?? '';
};


export const timeslotValidationSchema = (required: boolean = true) => {
  return yup
    .object()
    .shape({
      dayWithInterval: yup
        .array()
        .of(
          yup.object().shape({
            day: yup.string().required(),
            intervalError: yup.mixed().test({
              name: 'intervals.not.disjunct',
              test: (value: any, testContext) => {
                const parent = testContext.parent as GlobalDayWithInterval;

                if (parent.intervals && parent.intervals.length === 0) {
                  return true;
                }

                if (parent.openingMode === OpeningModeEnum.OPEN || parent.openingMode === OpeningModeEnum.CLOSED) {
                  return true;
                }

                const a = parent.intervals
                  .filter(t => t.sinceHoursString !== '' && t.tillHoursString !== '')
                  .sort((a, b) => a.sinceHoursString.localeCompare(b.sinceHoursString))
                  .map(t => {
                    return [t.sinceHoursString, t.tillHoursString];
                  })
                  .flat(1)
                  .filter(t => t !== '');

                const result = a.reduce((acc, cur) => {
                  if (acc <= cur) return cur;
                  else return 'Error';
                }, '00:00');

                return result !== 'Error';
              },
              message: 'required',
              exclusive: true,
            }),
            intervals: yup.array().of(
              yup.mixed().test({
                name: 'wrong.interval',
                test: (value: GlobalInterval, testContext) => {
                  // @ts-ignore
                  const parent = testContext.from[0].value as GlobalDayWithInterval;
                  if (parent.openingMode === OpeningModeEnum.CLOSED || parent.openingMode === OpeningModeEnum.OPEN) {
                    return true;
                  }
                  const since = value.sinceHoursString;
                  const till = value.tillHoursString;
                  if (validateHourString(since) && validateHourString(till)) {
                    // RD-2958: a zero-length interval (09:00-09:00) must stay valid, the Skoda public API sends it
                    if (since <= till) {
                      return true;
                    }
                  }
                  return false;
                },
                message: 'required',
                exclusive: true,
              }),
            ),
          }),
        )
        .min(required ? 1 : 0, 'required'),
    })
    .required();
};

/**
 * This is used for the managing of timeslots, delivery windows, etc. The component should use the opening hours, if specified.
 * @param entityId
 * @param entityType
 * @param timeslot
 * @param onChange
 * @param noOpeningHours
 * @constructor
 */
const ManageTimeslot: React.FC<ComponentProps> = ({
  entityId,
  entityType,
  timeslot = { 'dayWithInterval': [] },
  onChange,
  validationErrors,
  validationPrefix,
  externalLabel = false,
  value,
  dataTestId,
}) => {
  const [openingHours, setOpeningHours] = useStateCallback<OpeningHourDefinitionsDTO>(null);
  const { getOpeningHoursForEntity } = useContext(ApiContext);
  const [range, setRange] = useState<Date[] | Date>(() => {
    if (timeslot?.dayWithInterval?.length > 0) {
      console.log(`ManageTimeslot - initial timeslot:`, timeslot);

      const firstDay = dayjsFromLocalDateString(timeslot.dayWithInterval[0].utcDay);
      const lastDay = dayjsFromLocalDateString(timeslot.dayWithInterval[timeslot.dayWithInterval.length - 1].utcDay);

      console.log(`ManageTimeslot - firstDay:`, firstDay, ` lastDay:`, lastDay);

      if (!firstDay || !lastDay) return undefined;

      if (firstDay.isSame(lastDay, 'day')) return [firstDay.toDate()];

      return [firstDay.toDate(), lastDay.toDate()];
    }
    return undefined;
  });
  const { tr } = useTranslationLgs();
  const printedTimeslotPrefix = getTimeslotTestIdPrefix(dataTestId);

  /**
   * The entity the currently held days were derived against. Initialised to the mount-time entityId
   * so that loading an existing entity never re-derives its stored intervals — it only differs once
   * the user actually picks a different locality (including the very first one, picked after the
   * dates, which is what leaves every day CLOSED).
   */
  const derivedForEntityIdRef = useRef<string | undefined>(entityId);

  const cannotLoadOpeningHours = () => isNullOrUndefined(entityId) || isNullOrUndefined(entityType) || isNullOrUndefined(timeslot);

  useEffect(() => {
    // Without a locality there are no opening hours to speak of; drop the previous one's, otherwise
    // "Load opening hours" would offer another locality's times.
    if (cannotLoadOpeningHours()) {
      setOpeningHours(null);
      return;
    }
    getOpeningHoursForEntity(entityId, entityType, d => {
      setOpeningHours(d, state => {
        applyOpeningHoursToPickedDays(state);
      });
    });
    // Deliberately only the locality: the handler needs the current timeslot/onChange, and
    // getOpeningHoursForEntity gets a fresh identity on every ApiContext render (api.tsx builds the
    // value as {...ringilApiv2}), so listing them would refetch the opening hours on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  /**
   * Re-derives the already picked days once the locality's opening hours are known. The dates the
   * user chose are kept as they are; only openingMode and intervals follow the new locality. Picking
   * the dates before the locality would otherwise leave them permanently CLOSED (they are generated
   * against openingHours === null) and save that way.
   */
  const applyOpeningHoursToPickedDays = (openingHourDefinition: OpeningHourDefinitionsDTO) => {
    const localityChanged = derivedForEntityIdRef.current !== entityId;
    derivedForEntityIdRef.current = entityId;
    if (!localityChanged || haNoTimeslot()) return;

    const newTimeslot = produce(timeslot, draft => {
      draft.dayWithInterval.forEach(d => {
        const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
        const generatedHours = generateFromOpeningHours(openingHourDefinition, utcDay);
        d.openingMode = generatedHours.openingMode;
        d.intervals = generatedHours.intervals;
      });
    });
    onChange(newTimeslot);
  };

  // return dayjs in browser timezone!!!
  const normalizeInterval = (d: Date | Date[]): Dayjs[] => {
    if (Array.isArray(d)) {
      if (d[0] == d[1]) return [dayjs(d[0])];
      return [dayjs(d[0]), dayjs(d[1])];
    } else {
      return [dayjs(d)];
    }
  };

  // input date range in browser timezone !!!
  // luckily the calculation is against Monday - Sunday opening time, so we should be fine
  const regenerate = (range: Date[] | Date, openingHours?: OpeningHourDefinitionsDTO): GlobalTimeslots => {
    const normalizedRange = normalizeInterval(range);
    const days = dayJsInterval(normalizedRange[0], normalizedRange[1]);
    // days are in browser timezone!!!!
    return {
      dayWithInterval: days.map(day => {
        const openingHour = openingHours?.openingHoursDays?.find(oh => isSameDay(day, oh.day));
        const openingMode = openingHour?.openingMode ?? OpeningModeEnum.CLOSED;
        const intervals =
          openingHour?.openingHours.map(t => {
            return {
              sinceHoursString: t.since,
              tillHoursString: t.till,
            } as GlobalInterval;
          }) ?? [];

        return {
          day: day.toISOString(),
          utcDay: apiUtcDateFromDayjs(day),
          openingMode,
          intervals,
        };
      }),
      timezoneId: timeslot.timezoneId,
    };
  };

  const isSameDay = (day1: Dayjs, day2: DayEnum) => {
    return dayEnumFromNumber(day1.day()) === day2;
  };

  const haNoTimeslot = () => {
    return timeslot.dayWithInterval.length === 0;
  };

  const isValidDate = (d: unknown): d is Date => d instanceof Date && !isNaN(d.getTime());

  const changeRange = (range: Date[] | Date, openingHourDefinition: OpeningHourDefinitionsDTO) => {
    // Ignore unparseable typed input (PrimeReact hands a null/Invalid start in range mode): keep the
    // existing range rather than clearing it, and don't regenerate — regenerate -> day.toISOString()
    // would otherwise throw RangeError on an Invalid dayjs. A null END is fine (single-day selection,
    // dayJsInterval treats it as one day). (Sentry RINGIL-APP-AJ.)
    const start = Array.isArray(range) ? range[0] : range;
    if (!isValidDate(start)) return;
    setRange(range);
    const newTimeslots = regenerate(range, openingHourDefinition);
    onChange(newTimeslots);
  };

  const addInterval = (day: Dayjs) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.intervals.push({
          sinceHoursString: '',
          tillHoursString: '',
        });
      }
    });
    onChange(newTimeslot);
  };

  const removeDay = (day: Dayjs, position: number) => {
    // remove position-nth day
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval.find(
        d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.intervals.splice(position, 1);
      }
    });
    onChange(newTimeslot);
  };

  const generateFromOpeningHours = (openingHours: OpeningHourDefinitionsDTO, day: Dayjs): { intervals: GlobalInterval[]; openingMode: OpeningModeEnum } => {
    const openingHour = openingHours?.openingHoursDays?.find(oh => isSameDay(day, oh.day));
    if (isNullOrUndefined(openingHour)) return { openingMode: OpeningModeEnum.CLOSED, intervals: [] };

    if (openingHour.openingMode === OpeningModeEnum.CLOSED) return { intervals: [], openingMode: OpeningModeEnum.CLOSED };

    if (openingHour.openingMode === OpeningModeEnum.OPEN)
      return {
        intervals: [{ sinceHoursString: '00:00', tillHoursString: '24:00' }],
        openingMode: OpeningModeEnum.OPEN,
      };

    const intervals = openingHour?.openingHours.map(t => {
      return {
        sinceHoursString: t.since,
        tillHoursString: t.till,
      } as GlobalInterval;
    }) ?? [{ sinceHoursString: '', tillHoursString: '' }];

    return { intervals, openingMode: OpeningModeEnum.PARTIALLY_OPEN };
  };

  const openDay = (day: Dayjs) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        const dayWithIntervalUtcDay = dayWithInterval.utcDay ? dayjsFromApiUtc(dayWithInterval.utcDay) : dayjsFromApiPrague(dayWithInterval.day);
        const generatedHours = generateFromOpeningHours(openingHours, dayWithIntervalUtcDay);
        if (generatedHours.openingMode === OpeningModeEnum.CLOSED) {
          dayWithInterval.openingMode = OpeningModeEnum.PARTIALLY_OPEN;
          dayWithInterval.intervals = [{ sinceHoursString: '', tillHoursString: '' }];
        } else {
          dayWithInterval.openingMode = generatedHours.openingMode;
          dayWithInterval.intervals = generatedHours.intervals;
        }
      }
    });
    onChange(newTimeslot);
  };

  const closeDay = (day: Dayjs) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.openingMode = OpeningModeEnum.CLOSED;
        dayWithInterval.intervals = [];
      }
    });
    onChange(newTimeslot);
  };

  const toggleMode = (day: Dayjs) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.openingMode = dayWithInterval.openingMode == OpeningModeEnum.PARTIALLY_OPEN ? OpeningModeEnum.OPEN : OpeningModeEnum.PARTIALLY_OPEN;
        dayWithInterval.intervals = [{ sinceHoursString: '00:00', tillHoursString: '24:00' }];
      }
    });
    onChange(newTimeslot);
  };

  const changeSince = (day: Dayjs, position: number, since: string) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.intervals[position].sinceHoursString = since;
      }
    });
    onChange(newTimeslot);
  };

  const changeTill = (day: Dayjs, position: number, till: string) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        dayWithInterval.intervals[position].tillHoursString = till;
      }
    });
    onChange(newTimeslot);
  };

  const loadFromOpeningHours = (day: Dayjs) => {
    const newTimeslot = produce(timeslot, draft => {
      const dayWithInterval = draft.dayWithInterval
        .find(d => {
          const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
          return day.isSame(utcDay, 'day')
        });
      if (dayWithInterval) {
        const dayWithIntervalUtcDay = dayWithInterval.utcDay ? dayjsFromApiUtc(dayWithInterval.utcDay) : dayjsFromApiPrague(dayWithInterval.day);
        const generatedHours = generateFromOpeningHours(openingHours, dayWithIntervalUtcDay);
        dayWithInterval.openingMode = generatedHours.openingMode;
        dayWithInterval.intervals = generatedHours.intervals;
      }
    });
    onChange(newTimeslot);
  };

  const differsFromOpeningHours = (day: Dayjs) => {
    if (isNullOrUndefined(openingHours)) return false;
    const dayWithInterval = timeslot.dayWithInterval
      .find(d => {
        const utcDay = d.utcDay ? dayjsFromApiUtc(d.utcDay) : dayjsFromApiPrague(d.day);
        return day.isSame(utcDay, 'day')
      });
    if (!dayWithInterval) return false;

    const dayWithIntervalUtcDay = dayWithInterval.utcDay ? dayjsFromApiUtc(dayWithInterval.utcDay) : dayjsFromApiPrague(dayWithInterval.day);
    const generatedHours = generateFromOpeningHours(openingHours, dayWithIntervalUtcDay);
    if (generatedHours.openingMode !== dayWithInterval.openingMode) return true;

    if (generatedHours.openingMode === OpeningModeEnum.CLOSED) return false;

    if (generatedHours.intervals.length !== dayWithInterval.intervals.length) return true;

    return generatedHours.intervals.some((interval, index) => {
      const tsInterval = dayWithInterval.intervals[index];
      return interval.sinceHoursString !== tsInterval.sinceHoursString || interval.tillHoursString !== tsInterval.tillHoursString;
    });
  };

  const { i18n } = useTranslation();

  return (
    <SlotsInner externalLabel={externalLabel}>
      <>
        {/*{dumpVars({ entityId, entityType, range, timeslot, openingHours })}*/}

        {timeslot ? (
          <>
            {entityType === OpeningEntityTypeEnum.LOCATION && <CompanyExceptionWarning customerLocationId={entityId} slots={timeslot} />}
            <div data-testid={dataTestId}>
              <Calendar
                showIcon={true}
                showButtonBar={true}
                clearButtonClassName="!hidden"
                dateFormat={shortDateFormat}
                selectionMode={'range'}
                value={range}
                onChange={e => changeRange(e.value, openingHours)}
                style={{ width: '100%' }}
                locale={i18n.language}
                hideOnDateTimeSelect={true}
              />
            </div>
            {isNullOrUndefined(openingHours) && <Note>{tr(`ManageTimeslot.localityHasNoOpeningTimes`, `Locality has no opening times`)}</Note>}
            {timeslot.dayWithInterval?.map((ts, tsIndex) => {
              const utcDay = getTimeslotDay(ts);
              const dayTestIds = getPrintedTimeslotDayTestIds(printedTimeslotPrefix, tsIndex);
              return (
                <DayInner key={tsIndex} data-testid={dayTestIds.container}>
                  <RowWithSpaceBetween>
                    <Day data-testid={dayTestIds.date}>
                      {formatDateFromDayjsWithLocale(utcDay)} ({tr(`DayInWeek${utcDay.day()}`, `DayInWeek${utcDay.day()}`)})
                    </Day>
                    <RowWithGap>
                      {differsFromOpeningHours(utcDay) && (
                        <Button
                          label={tr(`ManageTimeslot.loadOpeningHours`, `Load opening hours`)}
                          className={'p-button-outlined'}
                          onClick={() => loadFromOpeningHours(utcDay)}
                        />
                      )}
                    </RowWithGap>
                  </RowWithSpaceBetween>
                  {ts.openingMode === OpeningModeEnum.CLOSED && (
                    <CenteredRowWithGap>
                      <span data-testid={dayTestIds.status}>{tr(`OpeningHourDefinitionForm.isClosed`, `Is closed`)}</span>
                      <div data-testid={dayTestIds.openButton}>
                        <Button label={tr(`OpeningHourDefinitionForm.open`, `Open`)}
                                className={'p-button-outlined'}
                                onClick={() => {
                                  const utcDay = ts.utcDay ? dayjsFromApiUtc(ts.utcDay) : dayjsFromApiPrague(ts.day);
                                  openDay(utcDay)
                        }}
                        />
                      </div>
                    </CenteredRowWithGap>
                  )}
                  {ts.openingMode === OpeningModeEnum.OPEN && (
                    <FourGrid>
                      <InputHour value={'00:00'} readOnly={true} disabled={true} onChange={() => {}} dataTestId={dayTestIds.intervalFrom(0)} />
                      <InputHour value={'24:00'} readOnly={true} disabled={true} onChange={() => {}} dataTestId={dayTestIds.intervalTo(0)} />
                      <CheckboxWithLabel
                        checkboxLabel={tr(`ManageTimeslot.openNonStop`, `Open non-stop`)}
                        value={true}
                        dataTestId={dayTestIds.openNonStopCheckbox}
                        onChange={() => {
                          const utcDay = ts.utcDay ? dayjsFromApiUtc(ts.utcDay) : dayjsFromApiPrague(ts.day);
                          toggleMode(utcDay);
                        }}
                        disabled={false}
                      />
                      <div data-testid={dayTestIds.closedButton}>
                        <Button label={tr(`ManageTimeslot.itIsClosed`, `It is closed`)}
                                className={'p-button-outlined'}
                                onClick={() => {
                                  const utcDay = ts.utcDay ? dayjsFromApiUtc(ts.utcDay) : dayjsFromApiPrague(ts.day);
                                  closeDay(utcDay)
                          }}
                        />
                      </div>
                    </FourGrid>
                  )}
                  {ts.openingMode === OpeningModeEnum.PARTIALLY_OPEN && (
                    <>
                      {ts.intervals?.map((hours, hIndex) => {
                        return (
                          <React.Fragment key={hIndex}>
                            <FourGrid>
                              <InputHour
                                value={hours.sinceHoursString}
                                readOnly={ts.openingMode === OpeningModeEnum.OPEN}
                                disabled={ts.openingMode === OpeningModeEnum.OPEN}
                                dataTestId={dayTestIds.intervalFrom(hIndex)}
                                onChange={v => changeSince(utcDay, hIndex, v)}
                              />
                              <InputHour
                                value={hours.tillHoursString}
                                readOnly={ts.openingMode === OpeningModeEnum.OPEN}
                                disabled={ts.openingMode === OpeningModeEnum.OPEN}
                                dataTestId={dayTestIds.intervalTo(hIndex)}
                                onChange={v => changeTill(utcDay, hIndex, v)}
                              />

                              {hIndex === 0 ? (
                                <CheckboxWithLabel
                                  checkboxLabel={tr(`ManageTimeslot.openNonStop`, `Open non-stop`)}
                                  value={ts.openingMode === OpeningModeEnum.OPEN}
                                  dataTestId={dayTestIds.openNonStopCheckbox}
                                  onChange={() => {
                                    toggleMode(utcDay);
                                  }}
                                  disabled={false}
                                />
                              ) : (
                                <div></div>
                              )}

                              {hIndex === 0 ? (
                                <div data-testid={dayTestIds.closedButton}>
                                  <Button
                                    label={tr(`ManageTimeslot.itIsClosed`, `It is closed`)}
                                    className={'p-button-outlined'}
                                    onClick={() => closeDay(utcDay)}
                                  ></Button>
                                </div>
                              ) : (
                                <Button
                                  data-testid={dayTestIds.removeIntervalButton(hIndex)}
                                  label={tr(`ManageTimeslot.remove`, `Remove`)}
                                  className={'p-button-outlined'}
                                  onClick={() => removeDay(utcDay, hIndex)}
                                ></Button>
                              )}
                            </FourGrid>
                            <ValidationDiv errors={validationErrors} path={`${validationPrefix}dayWithInterval[${tsIndex}].intervals[${hIndex}]`} />
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                  <ValidationDiv errors={validationErrors} path={`${validationPrefix}dayWithInterval[${tsIndex}].intervalError`} />
                  {ts.openingMode === OpeningModeEnum.PARTIALLY_OPEN && (
                    <AddWrapper>
                      <Clickable
                        data-testid={dayTestIds.addWorkingHoursButton}
                        onClick={() => addInterval(utcDay)}
                      >
                        {tr(`OpeningHourDefinitionForm.addWorkingHours`, `+ add working hours`)}
                      </Clickable>
                    </AddWrapper>
                  )}
                </DayInner>
              );
            })}
          </>
        ) : (
          <div></div>
        )}
      </>
    </SlotsInner>
  );
};

export const printDeliverySlots = (timeslots: GlobalTimeslots, tr: any, dataTestId?: string) => {
  const printedTimeslotPrefix = getTimeslotTestIdPrefix(dataTestId);

  return (
    <div>
      {timeslots.dayWithInterval.map((ts, tsIndex) => {
        const utcDay = getTimeslotDay(ts);
        const dayTestIds = getPrintedTimeslotDayTestIds(printedTimeslotPrefix, tsIndex);
        const intervalsText = getPrintedTimeslotIntervalsText(ts);
        return (
          <div key={tsIndex} data-testid={dayTestIds.container}>
            <CenteredRowWithGap>
              <span data-testid={dayTestIds.date}>{formatDateFromDayjsWithLocale(utcDay)}</span>
              {ts.openingMode === OpeningModeEnum.PARTIALLY_OPEN && <span data-testid={dayTestIds.intervals}>{intervalsText}</span>}
              {ts.openingMode === OpeningModeEnum.OPEN && <span data-testid={dayTestIds.intervals}>{tr(`ManageTimeslot.openNonStop`, `Open non-stop`)}</span>}
              {ts.openingMode === OpeningModeEnum.CLOSED && <span data-testid={dayTestIds.intervals}>{tr(`ManageTimeslot.closed`, `Closed`)}</span>}
            </CenteredRowWithGap>
            <COTimezone timezoneId={timeslots.timezoneId} dataTestId={dayTestIds.timezone} />
          </div>
        );
      })}
    </div>
  );
};



const FourGrid = styled.div`
  display: grid;
  grid-template-columns: 80px 80px 1fr 120px;
  gap: 0.5rem;
  margin: 0.3rem 0;
`;

const Day = styled.div`
  display: flex;
  font-weight: bold;
  font-size: 1.1rem;
`;

const DayInner = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
  gap: 0.5rem;
`;

const SlotsInner = styled.div<{ externalLabel: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
  padding: 1rem;
  background-color: #e9ecef;
  ${props =>
    props.externalLabel &&
    css`
      width: 100%;
      margin: 0rem 0;
    `}
`;

const AddWrapper = styled.div`
  display: flex;
  margin-top: 1rem;
`;
export default ManageTimeslot;
